import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Film, Loader2, MessageSquarePlus, CheckCircle2, PenLine } from "lucide-react";
import { api, filmVideoUrl, fmtTime } from "@/lib/api";

export default function FilmReview() {
  const { token } = useParams();
  const vid = useRef(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState(localStorage.getItem("ws_review_name") || "");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.film(token).then(setD).catch((e) => setErr(e?.response?.data?.detail || "Link not available"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="min-h-screen flex items-center justify-center text-[#94A3B8]" data-testid="film-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const remember = () => localStorage.setItem("ws_review_name", name);
  const comment = async () => {
    setBusy(true); remember();
    try { await api.filmComment(token, { t: vid.current?.currentTime || 0, text, name }); setText(""); await load(); } finally { setBusy(false); }
  };
  const decide = async (decision) => {
    setBusy(true); remember();
    try { await api.filmDecision(token, { decision, name, note }); await load(); } finally { setBusy(false); }
  };
  const seek = (t) => { if (vid.current) { vid.current.currentTime = t; vid.current.play(); } };
  const couple = [d.project?.bride, d.project?.groom].filter(Boolean).join(" & ");

  return (
    <div className="min-h-screen bg-[#0a0b10] text-[#F1F5F9] px-6 md:px-12 py-10 fade-up" data-testid="film-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <div className="flex items-center gap-2 metric-label"><Film size={13} className="text-[#E6B04E]" />{couple}{d.project?.venue ? ` · ${d.project.venue}` : ""}</div>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="film-title">{d.title}</h1>
          {d.message && <p className="text-[#94A3B8] mt-2">{d.message}</p>}
        </div>
        <div className="grid lg:grid-cols-[1.7fr_1fr] gap-6">
          <video ref={vid} src={filmVideoUrl(token)} controls preload="metadata" className="w-full rounded-2xl bg-black aspect-video" data-testid="film-player" />
          <div className="studio-card p-4 flex flex-col gap-3 max-h-[520px]">
            <div className="font-semibold text-sm">Notes on the film ({d.comments.length})</div>
            <ul className="flex-1 overflow-y-auto space-y-2" data-testid="film-comments">
              {[...d.comments].sort((a, b) => a.t - b.t).map((c) => (
                <li key={c.id} className={`text-sm ${c.resolved ? "opacity-50" : ""}`}>
                  <button className="font-mono text-[#E6B04E] mr-2 underline" onClick={() => seek(c.t)} data-testid={`film-comment-seek-${c.id}`}>{fmtTime(c.t)}</button>
                  <b>{c.name}</b>: {c.text}{c.resolved ? " ✓ done" : ""}
                </li>
              ))}
              {!d.comments.length && <li className="text-xs text-[#64748B]">Pause anywhere and add a note — it's saved with the exact time.</li>}
            </ul>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="studio-input !py-1.5 text-sm" data-testid="film-name" />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="e.g. Please use a different shot of the entry here" className="studio-input text-sm" data-testid="film-comment-input" />
            <button className="btn btn-ghost !py-1.5 text-sm" disabled={!text.trim() || busy} onClick={comment} data-testid="film-comment-submit"><MessageSquarePlus size={14} />Add note at current time</button>
          </div>
        </div>
        <div className="studio-card p-5 space-y-3 max-w-3xl" data-testid="film-decision">
          {d.status !== "pending" && (
            <p className={`text-sm flex items-center gap-2 ${d.status === "approved" ? "text-[#34d399]" : "text-[#fbbf24]"}`} data-testid="film-status">
              {d.status === "approved" ? <CheckCircle2 size={16} /> : <PenLine size={16} />}
              {d.status === "approved" ? "Approved" : "Changes requested"} by {d.decision?.name} — you can still update your decision.
            </p>
          )}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Overall message for your editor (optional)" className="studio-input text-sm w-full" data-testid="film-note" />
          <div className="flex gap-3 flex-wrap">
            <button className="btn btn-gold" disabled={busy} onClick={() => decide("approved")} data-testid="film-approve"><CheckCircle2 size={15} />Approve film</button>
            <button className="btn btn-ghost" disabled={busy} onClick={() => decide("changes_requested")} data-testid="film-request-changes"><PenLine size={15} />Request changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}
